import React, { useContext, useEffect, useMemo, useState } from "react";
import PageLayout from "../../../layouts/PageLayout";
import "../../../assets/fonts/ExpenseList.css";
import { SidebarContext } from "../../../context/Sidebar";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

/* ---------------------------------------------------------------- */
/* TEMP dummy data — remove once /expenses (GET) API is ready        */
/* ---------------------------------------------------------------- */
const DUMMY_CATEGORIES = [
  { id: 1, name: "Shop Rent" },
  { id: 2, name: "Electricity Bill" },
  { id: 3, name: "Staff Salary" },
  { id: 4, name: "Tea / Refreshment" },
  { id: 5, name: "Transport / Fuel" },
  { id: 6, name: "Packing Material" },
  { id: 7, name: "Office Stationery" },
  { id: 8, name: "Repair & Maintenance" },
  { id: 9, name: "Advertising" },
  { id: 10, name: "Miscellaneous" },
];

const DUMMY_BANKS = [
  { id: 1, name: "Meezan Bank - 0123456789" },
  { id: 2, name: "HBL - 9988776655" },
  { id: 3, name: "UBL - 1122334455" },
  { id: 4, name: "Allied Bank - 5566778899" },
  { id: 5, name: "Bank Alfalah - 3344556677" },
];

const DUMMY_EXPENSES = [
  {
    id: "exp-1",
    expense_date: "2026-07-20",
    comment: "Shop electricity bill for July",
    categories: [
      { category_id: 2, amount: 8500 },
      { category_id: 10, amount: 500 },
    ],
    payments: [{ method: "cash", amount: 9000 }],
  },
  {
    id: "exp-2",
    expense_date: "2026-07-21",
    comment: "Staff salary - front desk",
    categories: [{ category_id: 3, amount: 35000 }],
    payments: [
      { method: 1, amount: 20000 },
      { method: "cash", amount: 15000 },
    ],
  },
  {
    id: "exp-3",
    expense_date: "2026-07-22",
    comment: "Fuel + packing material",
    categories: [
      { category_id: 5, amount: 3000 },
      { category_id: 6, amount: 1200 },
    ],
    payments: [{ method: 2, amount: 4200 }],
  },
];

/* ---------------------------------------------------------------- */
/* Helpers                                                            */
/* ---------------------------------------------------------------- */

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/* ---------------------------------------------------------------- */
/* Main component                                                    */
/* ---------------------------------------------------------------- */

export default function ExpenseList() {
  const [expenses, setExpenses] = useState(DUMMY_EXPENSES);
  const [categories, setCategories] = useState(DUMMY_CATEGORIES);
  const [banks, setBanks] = useState(DUMMY_BANKS);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const { setSidebar } = useContext(SidebarContext);
  const navigate = useNavigate();

  useEffect(() => {
    setSidebar(false);
  }, [setSidebar]);

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/expenses`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );
      setExpenses(response.data?.expenses || []);
      setCategories(response.data?.categories || DUMMY_CATEGORIES);
      setBanks(response.data?.banks || DUMMY_BANKS);
    } catch (error) {
      console.log(error, "error fetching expenses list");
      // Keep dummy data visible if the API isn't ready yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, []);

  const categoryName = (id) =>
    categories.find((c) => c.id == id)?.name || "Category";

  const paymentLabel = (method) => {
    if (method === "cash") return "Cash";
    return banks.find((b) => b.id == method)?.name || "Bank";
  };

  const rowTotal = (row) =>
    row.categories.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

  const grandTotal = expenses.reduce((sum, e) => sum + rowTotal(e), 0);

  const handleDelete = (id) => {
    Swal.fire({
      icon: "warning",
      title: "Delete this expense?",
      text: "This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setDeletingId(id);
      axios
        .delete(`${process.env.REACT_APP_API_URL}/expenses/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })
        .then(() => {
          setExpenses((prev) => prev.filter((e) => e.id !== id));
        })
        .catch((err) => {
          console.log(err, "error deleting expense");
          Swal.fire({
            icon: "error",
            title: "Could not delete",
            text: "Please try again.",
          });
        })
        .finally(() => setDeletingId(null));
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return expenses;
    return expenses.filter((e) => {
      const comment = (e.comment || "").toLowerCase();
      return (
        comment.includes(q) ||
        formatDate(e.expense_date).toLowerCase().includes(q)
      );
    });
  }, [expenses, search]);

  return (
    <PageLayout>
      <div className="pe-scope">
        <div className="pe-page pb-5">
          <div className="container py-4" style={{ maxWidth: "1200px" }}>
            {/* Hero */}
            <div className="pe-hero mb-4 d-flex flex-wrap align-items-end justify-content-between gap-3">
              <div>
                <span className="pe-eyebrow">Accounts · Expenses</span>
                <h1 className="pe-title">Expense List</h1>
                <p className="pe-subtitle mb-0">
                  All recorded expenses, most recent first.
                </p>
              </div>
              <button
                type="button"
                className="pe-review-btn"
                onClick={() => navigate("/daily-expense-create")}
              >
                <i className="fa-solid fa-circle-plus"></i> Add Expense
              </button>
            </div>

            {/* Search */}
            {expenses.length > 0 && (
              <div className="pe-search-bar mb-4">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  type="text"
                  placeholder="Search by comment or date..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button type="button" onClick={() => setSearch("")}>
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                )}
              </div>
            )}

            {/* Loading state */}
            {loading ? (
              <div className="pe-empty-state">
                <i className="fa-solid fa-spinner fa-spin"></i>
                <h5>Loading expenses…</h5>
              </div>
            ) : filtered.length === 0 ? (
              /* Empty state */
              <div className="pe-empty-state">
                <i className="fa-solid fa-box-open"></i>
                <h5>
                  {expenses.length === 0
                    ? "No expenses yet"
                    : "No matches found"}
                </h5>
                <p>
                  {expenses.length === 0
                    ? "Once you record an expense, it will show up here."
                    : "Try a different search term."}
                </p>
                {expenses.length === 0 && (
                  <button
                    type="button"
                    className="pe-review-btn"
                    onClick={() => navigate("/daily-expense-create")}
                  >
                    <i className="fa-solid fa-circle-plus"></i> Add Expense
                  </button>
                )}
              </div>
            ) : (
              <div className="pe-table-wrap">
                <table className="pe-table">
                  <thead>
                    <tr>
                      <th className="pe-th-num">#</th>
                      <th>Date</th>
                      <th>Comment</th>
                      <th>Categories &amp; Amount</th>
                      <th>Paid Via</th>
                      <th className="text-end">Total</th>
                      <th className="pe-th-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((exp, index) => (
                      <tr className="pe-row" key={exp.id}>
                        <td className="pe-th-num text-muted">{index + 1}</td>
                        <td className="text-muted">
                          {formatDate(exp.expense_date)}
                        </td>
                        <td className="fw-semibold">{exp.comment || "—"}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            {exp.categories.map((c, i) => (
                              <span className="pe-chip pe-chip-gold" key={i}>
                                {categoryName(c.category_id)}
                                <strong>
                                  {" "}
                                  · {Number(c.amount).toFixed(2)}
                                </strong>
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            {exp.payments.map((p, i) => (
                              <span className="pe-chip pe-chip-teal" key={i}>
                                {paymentLabel(p.method)}
                                <strong>
                                  {" "}
                                  · {Number(p.amount).toFixed(2)}
                                </strong>
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="text-end fw-semibold pe-cell-accent">
                          {rowTotal(exp).toFixed(2)}
                        </td>
                        <td className="pe-th-actions">
                          <button
                            type="button"
                            className="pe-icon-btn pe-icon-btn-danger"
                            disabled={deletingId === exp.id}
                            onClick={() => handleDelete(exp.id)}
                            title="Delete this expense"
                          >
                            {deletingId === exp.id ? (
                              <i className="fa-solid fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fa-solid fa-trash"></i>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} className="text-end pe-hint">
                        Grand Total
                      </td>
                      <td className="text-end pe-cell-accent fw-bold">
                        {grandTotal.toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}